import { useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ShieldAlert } from 'lucide-react';
import { UserRole, userRoleSchema } from '@nbh/shared';

/**
 * i18n key per role, so a role never reaches the screen as its raw enum value.
 *
 * Lives here rather than in `@nbh/shared` because these labels are this surface's
 * wording for the tiers, not part of the API contract.
 */
export const ROLE_LABEL_KEY: Record<UserRole, string> = {
  [UserRole.SuperAdmin]: 'superAdmin.roleName.superAdmin',
  [UserRole.Admin]: 'superAdmin.roleName.admin',
  [UserRole.Supervisor]: 'superAdmin.roleName.supervisor',
  [UserRole.FrontDesk]: 'superAdmin.roleName.frontDesk',
  [UserRole.Housekeeping]: 'superAdmin.roleName.housekeeping',
  [UserRole.Maintenance]: 'superAdmin.roleName.maintenance',
  [UserRole.Technician]: 'superAdmin.roleName.technician',
  [UserRole.IT]: 'superAdmin.roleName.it',
  [UserRole.Staff]: 'superAdmin.roleName.staff',
  [UserRole.Auditor]: 'superAdmin.roleName.auditor',
  [UserRole.ExternalVendor]: 'superAdmin.roleName.externalVendor',
};

/** Falls back to the least-privileged role rather than guessing, if the API sends one we do not know. */
function asRole(value: string): UserRole {
  const parsed = userRoleSchema.safeParse(value);
  return parsed.success ? parsed.data : UserRole.ExternalVendor;
}

export interface ConfirmWithReasonProps {
  /** Named in the heading, so a mis-tap is obvious before it is confirmed. */
  subject: string;
  headingKey: string;
  explainKey: string;
  reasonLabelKey: string;
  reasonPlaceholderKey: string;
  confirmKey: string;
  workingKey: string;
  /** Extra controls — a role picker, say — shown above the reason field. */
  children?: ReactNode;
  /** Keeps confirm closed while the extra controls are still incomplete. */
  blocked?: boolean;
  blockedHint?: string;
  /**
   * Marks the act as irreversible (D25), which changes how the dialog looks rather
   * than what it does. Deactivating and deleting read almost identically once a
   * dialog is open, and the one that cannot be undone must not be the one that looks
   * like every other confirmation.
   */
  danger?: boolean;
  onConfirm: (reason: string) => Promise<void>;
  onCancel: () => void;
}

/**
 * Confirms a privileged act and captures why.
 *
 * `RetireDialog` does the same job for equipment and is reused verbatim for places.
 * It does not fit here: its copy retires a *thing* ("Retire Jane Doe?", suggestions
 * of "Replaced" and "Removed from site"), and a role change needs a field inside the
 * dialog that it has no slot for. The rule it enforces is the one that matters and is
 * kept identical — a typed reason of at least three characters before confirm enables.
 */
export function ConfirmWithReason({
  subject,
  headingKey,
  explainKey,
  reasonLabelKey,
  reasonPlaceholderKey,
  confirmKey,
  workingKey,
  children,
  blocked = false,
  blockedHint,
  danger = false,
  onConfirm,
  onCancel,
}: ConfirmWithReasonProps) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ready = reason.trim().length >= 3 && !blocked && !busy;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!ready) return;

    setBusy(true);
    setError(null);
    try {
      await onConfirm(reason.trim());
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t('superAdmin.failed'));
      setBusy(false);
    }
  }

  return (
    // Bottom-anchored on a phone so the confirm lands under the thumb, centred once
    // there is room for it.
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 p-3 sm:items-center">
      <form
        onSubmit={submit}
        role="dialog"
        aria-modal="true"
        aria-label={t(headingKey, { subject })}
        className={`max-h-full w-full max-w-md overflow-y-auto rounded-xl border bg-slate-950 p-4 ${
          danger ? 'border-red-600' : 'border-amber-700/60'
        }`}
      >
        <p className="flex items-start gap-2 text-sm font-semibold text-slate-100">
          <ShieldAlert className="mt-0.5 size-4 shrink-0 text-amber-400" aria-hidden="true" />
          {t(headingKey, { subject })}
        </p>
        <p className="mt-1 text-xs text-slate-400">{t(explainKey)}</p>

        {children}

        <input
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder={t(reasonPlaceholderKey)}
          aria-label={t(reasonLabelKey)}
          maxLength={500}
          autoFocus
          className="mt-3 w-full rounded border border-slate-700 bg-slate-900 px-3 py-3 text-sm"
        />

        {blocked && blockedHint && <p className="mt-2 text-xs text-amber-300">{blockedHint}</p>}

        {error && (
          <p role="alert" className="mt-2 text-sm text-red-400">
            {error}
          </p>
        )}

        <div className="mt-4 flex gap-2">
          <button
            type="submit"
            disabled={!ready}
            className="min-h-[48px] flex-1 rounded-lg bg-red-700 text-sm font-semibold text-white disabled:opacity-40"
          >
            {busy ? t(workingKey) : t(confirmKey)}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="min-h-[48px] rounded-lg bg-transparent px-4 text-sm text-slate-400"
          >
            {t('superAdmin.cancel')}
          </button>
        </div>
      </form>
    </div>
  );
}

/**
 * Moves an account between tiers.
 *
 * The picker offers every role including SUPER_ADMIN — this is the one surface that
 * may grant root, which is what makes the tier real rather than decorative.
 */
/**
 * Confirms an irreversible delete (decision D25).
 *
 * The typed field is the whole point. Deactivating and deleting are one row apart in
 * the list and read almost identically once a dialog is open, so the destructive one
 * asks for something a mis-tap cannot produce: the target's own name or email, typed
 * out. The server checks the same value again — this is a courtesy to the operator,
 * not the control.
 *
 * `danger` turns the frame red, so the dialog does not look like every other
 * confirmation before it is read.
 */
export function DeleteDialog({
  subject,
  /** The exact string that must be typed — an email for an account, a name for a place. */
  confirmValue,
  explainKey,
  onConfirm,
  onCancel,
}: {
  subject: string;
  confirmValue: string;
  explainKey: string;
  onConfirm: (reason: string, confirm: string) => Promise<void>;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [typed, setTyped] = useState('');

  return (
    <ConfirmWithReason
      subject={subject}
      danger
      headingKey="superAdmin.delete.heading"
      explainKey={explainKey}
      reasonLabelKey="superAdmin.delete.reason"
      reasonPlaceholderKey="superAdmin.delete.reasonPlaceholder"
      confirmKey="superAdmin.delete.confirm"
      workingKey="superAdmin.delete.working"
      blocked={typed !== confirmValue}
      blockedHint={t('superAdmin.delete.mustMatch', { value: confirmValue })}
      onConfirm={(reason) => onConfirm(reason, typed)}
      onCancel={onCancel}
    >
      <label className="mt-3 block text-xs text-slate-400">
        {t('superAdmin.delete.typeToConfirm', { value: confirmValue })}
        <input
          value={typed}
          onChange={(event) => setTyped(event.target.value)}
          // Autocorrect on a phone will happily "fix" a room name or an address.
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          aria-label={t('superAdmin.delete.typeToConfirm', { value: confirmValue })}
          className="mt-1 min-h-[48px] w-full rounded border border-red-800 bg-slate-900 px-3 text-sm text-slate-100"
        />
      </label>
    </ConfirmWithReason>
  );
}

export function RoleChangeDialog({
  subject,
  currentRole,
  onConfirm,
  onCancel,
}: {
  subject: string;
  currentRole: string;
  onConfirm: (role: UserRole, reason: string) => Promise<void>;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [role, setRole] = useState<UserRole>(asRole(currentRole));

  return (
    <ConfirmWithReason
      subject={subject}
      headingKey="superAdmin.role.heading"
      explainKey="superAdmin.role.explain"
      reasonLabelKey="superAdmin.role.reason"
      reasonPlaceholderKey="superAdmin.role.reasonPlaceholder"
      confirmKey="superAdmin.role.confirm"
      workingKey="superAdmin.role.working"
      blocked={role === currentRole}
      blockedHint={t('superAdmin.role.unchanged')}
      onConfirm={(reason) => onConfirm(role, reason)}
      onCancel={onCancel}
    >
      <label className="mt-3 block text-xs text-slate-400">
        {t('superAdmin.role.newRole')}
        <select
          value={role}
          onChange={(event) => setRole(asRole(event.target.value))}
          className="mt-1 min-h-[48px] w-full rounded border border-slate-700 bg-slate-900 px-2 text-sm text-slate-100"
        >
          {userRoleSchema.options.map((option) => (
            <option key={option} value={option}>
              {t(ROLE_LABEL_KEY[option])}
            </option>
          ))}
        </select>
      </label>
    </ConfirmWithReason>
  );
}
